<script>
import { required, email } from "vuelidate/lib/validators";

import {
  authMethods,
  notificationMethods
} from "@/state/helpers";

export default {
  data() {
    return {
      email: "",
      password: "",
      submitted: false,
      tryingToLogIn: false,
      authError: null,
      isAuthError: false
    };
  },
  computed: {
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
    isLoggedIn() {
      return this.$store.getters['auth/loggedIn'];
    }
  },
  created() {
    document.body.classList.add("auth-body-bg");
    
    // Redirect if already logged in
    if (this.isLoggedIn) {
      this.$router.push({ name: "home" });
    }
  },
  validations: {
    email: { required, email },
    password: { required }
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    // Try to log the user in with the username
    // and password they provided.
    async tryToLogIn() {
      this.submitted = true;
      this.isAuthError = false;
      this.authError = null;
      
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.tryingToLogIn = true;

      try {
        const { email, password } = this;
        
        await this.logIn({ email, password });
        
        this.tryingToLogIn = false;
        
        // Redirect to the originally requested page, or to the home page
        this.$router.push(
          this.$route.query.redirectFrom || { name: "home" }
        );
        
      } catch (error) {
        this.tryingToLogIn = false;
        
        // Log error for debugging
        console.error('Login error:', error);
        
        // Extract error message properly
        let errorMessage = "Erro ao fazer login";
        
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        
        this.authError = errorMessage;
        this.isAuthError = true;
        
        // Don't prevent default or return false - just show error
      }
    }
  }
};
</script>

<template>
  <div>
    <div class="home-btn d-none d-sm-block">
      <a href="/">
        <i class="mdi mdi-home-variant h2 text-white"></i>
      </a>
    </div>
    <div>
      <div class="container-fluid p-0">
        <div class="row no-gutters">
          <div class="col-lg-4">
            <div class="authentication-page-content p-4 d-flex align-items-center min-vh-100">
              <div class="w-100">
                <div class="row justify-content-center">
                  <div class="col-lg-9">
                    <div>
                      <div class="text-center">
                        <div>
                          <a href="/" class="logo">
                            <svg width="220" height="60" viewBox="0 0 220 60" xmlns="http://www.w3.org/2000/svg">
                              <!-- Gradiente para o círculo principal -->
                              <defs>
                                <linearGradient id="mainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" style="stop-color:#1976d2;stop-opacity:1" />
                                  <stop offset="100%" style="stop-color:#0d47a1;stop-opacity:1" />
                                </linearGradient>
                                <linearGradient id="connectGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" style="stop-color:#1976d2;stop-opacity:1" />
                                  <stop offset="100%" style="stop-color:#43a047;stop-opacity:1" />
                                </linearGradient>
                              </defs>
                              
                              <!-- Círculo principal com gradiente -->
                              <circle cx="35" cy="30" r="20" fill="url(#mainGrad)" />
                              
                              <!-- Ondas de rádio/conectividade -->
                              <path d="M35 8 Q60 30 35 52" stroke="#42a5f5" stroke-width="3" fill="none" opacity="0.8"/>
                              <path d="M35 12 Q50 30 35 48" stroke="#64b5f6" stroke-width="2.5" fill="none" opacity="0.9"/>
                              <path d="M35 16 Q45 30 35 44" stroke="#90caf9" stroke-width="2" fill="none"/>
                              
                              <!-- Ícone médico central (cruz + imagem) -->
                              <rect x="28" y="23" width="14" height="14" rx="3" fill="#fff" opacity="0.95"/>
                              <!-- Cruz médica -->
                              <rect x="33" y="25" width="4" height="10" fill="#1976d2"/>
                              <rect x="30" y="28" width="10" height="4" fill="#1976d2"/>
                              <!-- Linha de conexão/sinal -->
                              <circle cx="32" cy="27" r="1" fill="#43a047"/>
                              <circle cx="38" cy="33" r="1" fill="#43a047"/>
                              <line x1="32" y1="27" x2="38" y2="33" stroke="#43a047" stroke-width="1"/>
                              
                              <!-- Pontos de conexão ao redor -->
                              <circle cx="65" cy="20" r="2" fill="#42a5f5" opacity="0.7"/>
                              <circle cx="68" cy="35" r="2" fill="#66bb6a" opacity="0.7"/>
                              <circle cx="63" cy="45" r="2" fill="#42a5f5" opacity="0.7"/>
                              
                              <!-- Linhas de conexão sutis -->
                              <line x1="55" y1="30" x2="65" y2="20" stroke="#e3f2fd" stroke-width="1" opacity="0.5"/>
                              <line x1="55" y1="30" x2="68" y2="35" stroke="#e8f5e8" stroke-width="1" opacity="0.5"/>
                              <line x1="55" y1="30" x2="63" y2="45" stroke="#e3f2fd" stroke-width="1" opacity="0.5"/>
                              
                              <!-- Texto do logo -->
                              <text x="80" y="25" font-family="Segoe UI, Arial, sans-serif" font-size="18" fill="#1976d2" font-weight="700">Med</text>
                              <text x="120" y="25" font-family="Segoe UI, Arial, sans-serif" font-size="12" fill="#1976d2" font-weight="700">sys</text>
                              <text x="80" y="45" font-family="Segoe UI, Arial, sans-serif" font-size="18" fill="url(#connectGrad)" font-weight="600">Health</text>
                              
                              <!-- Subtítulo pequeno -->
                              <text x="80" y="55" font-family="Segoe UI, Arial, sans-serif" font-size="8" fill="#666" opacity="0.8">Medical Imaging Platform</text>
                            </svg>
                          </a>
                        </div>

                        <p class="text-muted">Conectando tecnologia e cuidado médico</p>
                      </div>

                      <b-alert
                        variant="danger"
                        class="mt-3"
                        v-if="notification.message"
                        show
                        dismissible
                      >{{notification.message}}</b-alert>

                      <b-alert
                        variant="danger"
                        class="mt-3"
                        v-if="isAuthError && authError"
                        show
                        dismissible
                      >{{authError}}</b-alert>

                      <div class="p-2 mt-5">
                        <form class="form-horizontal" @submit.prevent="tryToLogIn">
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-mail-line auti-custom-input-icon"></i>
                            <label for="email">E-mail</label>
                            <input
                              type="email"
                              v-model="email"
                              class="form-control"
                              id="email"
                              placeholder="Digite seu e-mail"
                              :class="{ 'is-invalid': submitted && $v.email.$error }"
                            />
                            <div v-if="submitted && $v.email.$error" class="invalid-feedback">
                              <span v-if="!$v.email.required">E-mail é obrigatório.</span>
                              <span v-if="!$v.email.email">Por favor, insira um e-mail válido.</span>
                            </div>
                          </div>

                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-lock-2-line auti-custom-input-icon"></i>
                            <label for="userpassword">Senha</label>
                            <input
                              v-model="password"
                              type="password"
                              class="form-control"
                              id="userpassword"
                              placeholder="Digite sua senha"
                              :class="{ 'is-invalid': submitted && $v.password.$error }"
                            />
                            <div
                              v-if="submitted && !$v.password.required"
                              class="invalid-feedback"
                            >Senha é obrigatória.</div>
                          </div>

                          <div class="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              class="custom-control-input"
                              id="customControlInline"
                            />
                          </div>

                          <div class="mt-4 text-center">
                            <button
                              class="btn btn-primary w-md waves-effect waves-light"
                              type="submit"
                              :disabled="tryingToLogIn"
                            >
                              <span v-if="tryingToLogIn">
                                <i class="mdi mdi-loading mdi-spin mr-1"></i>
                                Entrando...
                              </span>
                              <span v-else>Entrar</span>
                            </button>
                          </div>                          
                        </form>
                      </div>

                      <div class="mt-5 text-center">                       
                        <p>
                          © 2020 MedsysHealth. Todos os direitos reservados.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-8">
            <div class="authentication-bg">
              <div class="bg-overlay"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>